import type {components} from "./generated";

export type OperationsStatus = components["schemas"]["OperationsStatus"];
export type WorkPackage = components["schemas"]["WorkPackage"];
export type WorkPackageDraft = components["schemas"]["WorkPackageDraft"];
export type WorkPackageValidation = components["schemas"]["WorkPackageValidation"];
export type WorkPackageVersion = components["schemas"]["WorkPackageVersion"];
export type WorkPackageApproval = components["schemas"]["WorkPackageApproval"];
export type Program = components["schemas"]["Program"];
export type ProgramDraft = components["schemas"]["ProgramDraft"];
export type Product = components["schemas"]["Product"];
export type ProductDraft = components["schemas"]["ProductDraft"];
export type Capability = components["schemas"]["Capability"];
export type CapabilityDraft = components["schemas"]["CapabilityDraft"];
export type GovernedLifecycleCommand = components["schemas"]["GovernedLifecycleCommand"];
export type ObjectContext = components["schemas"]["ObjectContext"];
export type AvailableAction = components["schemas"]["AvailableAction"];
export type ReadModelFoundation = components["schemas"]["ReadModelFoundation"];
export type ApproverIdentity = components["schemas"]["ApproverIdentity"];
export type ExecutionPlan = components["schemas"]["ExecutionPlan"];
export type ExecutionPlanDraft = components["schemas"]["ExecutionPlanDraft"];
export type ExecutionPlanValidation = components["schemas"]["ExecutionPlanValidation"];
export type ExecutionPlanVersion = components["schemas"]["ExecutionPlanVersion"];
export type ExecutionPlanDiff = components["schemas"]["ExecutionPlanDiff"];
export type ExecutionPlanApproval = components["schemas"]["ExecutionPlanApproval"];
export type ExecutionPlanAudit = components["schemas"]["ExecutionPlanAudit"];
export type AuthorizedFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

export async function getOperationsStatus(
  authorizedFetch: AuthorizedFetch,
  signal?: AbortSignal
): Promise<OperationsStatus> {
  const response = await authorizedFetch("/api/v1/status", {
    signal,
    headers: {Accept: "application/json"}
  });
  if (!response.ok) throw new Error(`Status read failed: ${response.status}`);
  return await response.json() as OperationsStatus;
}

export function unavailableStatus(reason: string): OperationsStatus {
  return {
    environment: "UNKNOWN",
    executionMode: "CONTROL_PLANE",
    execution: {
      mode: "CONTROL_PLANE",
      availability: "UNKNOWN",
      laneRequired: true,
      laneStatus: "UNKNOWN",
      laneIdentity: null,
      detail: reason
    },
    humanGate: "JWT_HUMAN_REQUIRED",
    health: "DOWN",
    observedAt: new Date().toISOString(),
    activeIncidents: [reason],
    dependencies: [
      {component: "backend", state: "DOWN", detail: reason}
    ]
  };
}

export async function listWorkPackages(
  authorizedFetch: AuthorizedFetch,
  signal?: AbortSignal
): Promise<WorkPackage[]> {
  return await json<WorkPackage[]>(authorizedFetch, "/api/v1/work-packages", {
    signal,
    headers: {Accept: "application/json"}
  });
}

export async function readWorkPackageRegistryActions(
  authorizedFetch: AuthorizedFetch,
  signal?: AbortSignal
): Promise<AvailableAction[]> {
  return await json<AvailableAction[]>(authorizedFetch, "/api/v1/work-packages/actions", {
    signal, headers: {Accept: "application/json"}
  });
}

export async function readWorkPackage(
  authorizedFetch: AuthorizedFetch,
  workPackageId: string,
  signal?: AbortSignal
): Promise<WorkPackage> {
  return await json<WorkPackage>(authorizedFetch,
    `/api/v1/work-packages/${encodeURIComponent(workPackageId)}`, {
      signal, headers: {Accept: "application/json"}
    });
}

export async function listPrograms(
  authorizedFetch: AuthorizedFetch,
  search = "",
  signal?: AbortSignal
): Promise<Program[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : "";
  return await json<Program[]>(authorizedFetch, `/api/v1/programs${query}`, {
    signal, headers: {Accept: "application/json"}
  });
}

export async function readProgramRegistryActions(
  authorizedFetch: AuthorizedFetch,
  signal?: AbortSignal
): Promise<AvailableAction[]> {
  return await json<AvailableAction[]>(authorizedFetch, "/api/v1/programs/actions", {
    signal, headers: {Accept: "application/json"}
  });
}

export async function readProgram(
  authorizedFetch: AuthorizedFetch,
  programId: string,
  signal?: AbortSignal
): Promise<Program> {
  return await json<Program>(authorizedFetch,
    `/api/v1/programs/${encodeURIComponent(programId)}`, {
      signal, headers: {Accept: "application/json"}
    });
}


export type ProgramRoadmapLink = components["schemas"]["ProgramRoadmapLink"];
export type ProgramRoadmapMilestone = components["schemas"]["ProgramRoadmapMilestone"];
export type ProgramRoadmap = components["schemas"]["ProgramRoadmap"];

export async function readProgramRoadmap(authorizedFetch: AuthorizedFetch, programId: string, signal?: AbortSignal): Promise<ProgramRoadmap> {
  return await json<ProgramRoadmap>(authorizedFetch, `/api/v1/programs/${encodeURIComponent(programId)}/roadmap`, {
    signal, headers: {Accept: "application/json"}
  });
}

export async function createProgram(
  authorizedFetch: AuthorizedFetch,
  draft: ProgramDraft
): Promise<Program> {
  return await authoring<Program>(authorizedFetch, "/api/v1/programs", {
    method: "POST", body: JSON.stringify(draft)
  });
}

export async function updateProgram(
  authorizedFetch: AuthorizedFetch,
  program: Program,
  draft: ProgramDraft
): Promise<Program> {
  return await authoring<Program>(authorizedFetch,
    `/api/v1/programs/${encodeURIComponent(program.id)}`, {
      method: "PUT",
      body: JSON.stringify({expectedVersion: program.version, draft})
    });
}

export async function transitionProgram(
  authorizedFetch: AuthorizedFetch,
  program: Program,
  action: "validate" | "submit" | "activate" | "reject" | "return-for-revision"
    | "revise" | "cancel" | "hold" | "resume" | "complete" | "archive",
  rationale = ""
): Promise<Program> {
  return await authoring<Program>(authorizedFetch,
    `/api/v1/programs/${encodeURIComponent(program.id)}/${action}`, {
      method: "POST", body: JSON.stringify({expectedVersion: program.version, rationale})
    });
}

export async function listProducts(
  authorizedFetch: AuthorizedFetch,
  search = "",
  signal?: AbortSignal
): Promise<Product[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : "";
  return await json<Product[]>(authorizedFetch, `/api/v1/products${query}`, {
    signal, headers: {Accept: "application/json"}
  });
}

export async function createProduct(
  authorizedFetch: AuthorizedFetch,
  draft: ProductDraft
): Promise<Product> {
  return await authoring<Product>(authorizedFetch, "/api/v1/products", {
    method: "POST", body: JSON.stringify(draft)
  });
}

export async function updateProduct(
  authorizedFetch: AuthorizedFetch,
  product: Product,
  draft: ProductDraft
): Promise<Product> {
  return await authoring<Product>(authorizedFetch,
    `/api/v1/products/${encodeURIComponent(product.id)}`, {
      method: "PUT", body: JSON.stringify({expectedVersion: product.version, draft})
    });
}

export async function transitionProduct(
  authorizedFetch: AuthorizedFetch,
  product: Product,
  action: "validate" | "submit" | "activate" | "revise",
  rationale = ""
): Promise<Product> {
  return await authoring<Product>(authorizedFetch,
    `/api/v1/products/${encodeURIComponent(product.id)}/${action}`, {
      method: "POST", body: JSON.stringify({expectedVersion: product.version, rationale})
    });
}

export async function listCapabilities(
  authorizedFetch: AuthorizedFetch,
  productId?: string,
  signal?: AbortSignal
): Promise<Capability[]> {
  const query = productId ? `?productId=${encodeURIComponent(productId)}` : "";
  return await json<Capability[]>(authorizedFetch, `/api/v1/capabilities${query}`, {
    signal, headers: {Accept: "application/json"}
  });
}

export async function createCapability(
  authorizedFetch: AuthorizedFetch,
  draft: CapabilityDraft
): Promise<Capability> {
  return await authoring<Capability>(authorizedFetch, "/api/v1/capabilities", {
    method: "POST", body: JSON.stringify(draft)
  });
}

export async function updateCapability(
  authorizedFetch: AuthorizedFetch,
  capability: Capability,
  draft: CapabilityDraft
): Promise<Capability> {
  return await authoring<Capability>(authorizedFetch,
    `/api/v1/capabilities/${encodeURIComponent(capability.id)}`, {
      method: "PUT", body: JSON.stringify({expectedVersion: capability.version, draft})
    });
}

export async function transitionCapability(
  authorizedFetch: AuthorizedFetch,
  capability: Capability,
  action: "validate" | "submit" | "activate" | "revise",
  rationale = ""
): Promise<Capability> {
  return await authoring<Capability>(authorizedFetch,
    `/api/v1/capabilities/${encodeURIComponent(capability.id)}/${action}`, {
      method: "POST", body: JSON.stringify({expectedVersion: capability.version, rationale})
    });
}

export async function readObjectContext(
  authorizedFetch: AuthorizedFetch,
  objectType: "PROGRAM" | "PRODUCT" | "CAPABILITY" | "WORK_PACKAGE" | "EXECUTION_PLAN",
  objectId: string,
  signal?: AbortSignal
): Promise<ObjectContext> {
  return await json<ObjectContext>(authorizedFetch,
    `/api/v1/contexts/${objectType}/${encodeURIComponent(objectId)}`, {
      signal, headers: {Accept: "application/json"}
    });
}

export async function readModelFoundations(
  authorizedFetch: AuthorizedFetch,
  signal?: AbortSignal
): Promise<ReadModelFoundation> {
  return await json<ReadModelFoundation>(authorizedFetch,
    "/api/v1/read-models/foundations", {
      signal, headers: {Accept: "application/json"}
    });
}

export async function createWorkPackage(
  authorizedFetch: AuthorizedFetch,
  draft: WorkPackageDraft
): Promise<WorkPackage> {
  return await authoring<WorkPackage>(authorizedFetch, "/api/v1/work-packages", {
    method: "POST",
    body: JSON.stringify(draft)
  });
}

export async function updateWorkPackage(
  authorizedFetch: AuthorizedFetch,
  workPackage: WorkPackage,
  draft: WorkPackageDraft
): Promise<WorkPackage> {
  return await authoring<WorkPackage>(
    authorizedFetch,
    `/api/v1/work-packages/${workPackage.id}`,
    {method: "PUT", body: JSON.stringify({
      expectedVersion: workPackage.version,
      draft
    })}
  );
}

export async function validateWorkPackage(
  authorizedFetch: AuthorizedFetch,
  workPackage: WorkPackage
): Promise<WorkPackageValidation> {
  return await command<WorkPackageValidation>(
    authorizedFetch, workPackage, "validate", "Validate governed package"
  );
}

export async function submitWorkPackage(
  authorizedFetch: AuthorizedFetch,
  workPackage: WorkPackage
): Promise<WorkPackage> {
  return await command<WorkPackage>(
    authorizedFetch, workPackage, "submit", "Submit to named human approver"
  );
}

export async function discardWorkPackage(
  authorizedFetch: AuthorizedFetch,
  workPackage: WorkPackage,
  rationale: string
): Promise<WorkPackage> {
  return await command<WorkPackage>(
    authorizedFetch, workPackage, "discard", rationale
  );
}

export async function listWorkPackageVersions(
  authorizedFetch: AuthorizedFetch,
  workPackageId: string
): Promise<WorkPackageVersion[]> {
  return await json<WorkPackageVersion[]>(authorizedFetch,
    `/api/v1/work-packages/${workPackageId}/versions`, {
      headers: {Accept: "application/json"}
    });
}

export async function listWorkPackageApprovers(
  authorizedFetch: AuthorizedFetch,
  signal?: AbortSignal
): Promise<ApproverIdentity[]> {
  return await json<ApproverIdentity[]>(authorizedFetch,
    "/api/v1/work-packages/approvers", {
      signal,
      headers: {Accept: "application/json"}
    });
}

export async function listWorkPackageApprovals(
  authorizedFetch: AuthorizedFetch,
  signal?: AbortSignal
): Promise<WorkPackageApproval[]> {
  return await json<WorkPackageApproval[]>(authorizedFetch,
    "/api/v1/work-packages/approval-inbox", {
      signal,
      headers: {Accept: "application/json"}
    });
}

export async function approveWorkPackage(
  authorizedFetch: AuthorizedFetch,
  approval: WorkPackageApproval,
  rationale: string
): Promise<WorkPackage> {
  return await approvalCommand(
    authorizedFetch, approval, "activate", rationale
  );
}

export async function rejectWorkPackage(
  authorizedFetch: AuthorizedFetch,
  approval: WorkPackageApproval,
  reason: string
): Promise<WorkPackage> {
  return await approvalCommand(
    authorizedFetch, approval, "reject", reason
  );
}

export async function returnWorkPackageForRevision(
  authorizedFetch: AuthorizedFetch,
  approval: WorkPackageApproval,
  reason: string
): Promise<WorkPackage> {
  return await approvalCommand(
    authorizedFetch, approval, "return-for-revision", reason
  );
}

export async function reviseWorkPackageApprover(
  authorizedFetch: AuthorizedFetch,
  workPackage: WorkPackage,
  namedApprover: string,
  rationale: string
): Promise<WorkPackage> {
  return await authoring<WorkPackage>(authorizedFetch,
    `/api/v1/work-packages/${workPackage.id}/revise-approver`, {
      method: "POST",
      body: JSON.stringify({
        expectedVersion: workPackage.version,
        namedApprover,
        rationale
      })
    });
}

async function approvalCommand(
  authorizedFetch: AuthorizedFetch,
  approval: WorkPackageApproval,
  action: "activate" | "reject" | "return-for-revision",
  rationale: string
): Promise<WorkPackage> {
  return await authoring<WorkPackage>(authorizedFetch,
    `/api/v1/work-packages/${approval.workPackageId}/${action}`, {
      method: "POST",
      body: JSON.stringify({
        expectedVersion: approval.exactVersion,
        rationale
      })
    });
}

export async function listExecutionPlans(
  authorizedFetch: AuthorizedFetch,
  workPackageId?: string,
  signal?: AbortSignal
): Promise<ExecutionPlan[]> {
  const query = workPackageId ? `?workPackageId=${encodeURIComponent(workPackageId)}` : "";
  return await json<ExecutionPlan[]>(authorizedFetch, `/api/v1/execution-plans${query}`, {
    signal, headers: {Accept: "application/json"}
  });
}

export async function readExecutionPlan(
  authorizedFetch: AuthorizedFetch,
  executionPlanId: string,
  signal?: AbortSignal
): Promise<ExecutionPlan> {
  return await json<ExecutionPlan>(authorizedFetch,
    `/api/v1/execution-plans/${encodeURIComponent(executionPlanId)}`, {
      signal, headers: {Accept: "application/json"}
    });
}

export async function createExecutionPlan(
  authorizedFetch: AuthorizedFetch,
  draft: ExecutionPlanDraft
): Promise<ExecutionPlan> {
  return await authoring<ExecutionPlan>(authorizedFetch, "/api/v1/execution-plans", {
    method: "POST", body: JSON.stringify(draft)
  });
}

export async function updateExecutionPlan(
  authorizedFetch: AuthorizedFetch,
  plan: ExecutionPlan,
  draft: ExecutionPlanDraft,
  changeReason: string
): Promise<ExecutionPlan> {
  return await authoring<ExecutionPlan>(authorizedFetch, `/api/v1/execution-plans/${plan.id}`, {
    method: "PUT", body: JSON.stringify({expectedVersion: plan.version, changeReason, draft})
  });
}

export async function validateExecutionPlan(
  authorizedFetch: AuthorizedFetch,
  plan: ExecutionPlan
): Promise<ExecutionPlanValidation> {
  return await executionPlanCommand<ExecutionPlanValidation>(
    authorizedFetch, plan, "validate", "Validate task graph and governance controls"
  );
}

export async function submitExecutionPlan(
  authorizedFetch: AuthorizedFetch,
  plan: ExecutionPlan
): Promise<ExecutionPlan> {
  return await executionPlanCommand<ExecutionPlan>(
    authorizedFetch, plan, "submit", "Submit exact version and content hash for approval"
  );
}

export async function decideExecutionPlan(
  authorizedFetch: AuthorizedFetch,
  plan: ExecutionPlan,
  action: "approve" | "reject" | "return-for-revision",
  rationale: string
): Promise<ExecutionPlan> {
  return await executionPlanCommand<ExecutionPlan>(authorizedFetch, plan, action, rationale);
}

export async function cancelExecutionPlan(
  authorizedFetch: AuthorizedFetch,
  plan: ExecutionPlan,
  rationale: string
): Promise<ExecutionPlan> {
  return await executionPlanCommand<ExecutionPlan>(authorizedFetch, plan, "cancel", rationale);
}

export async function listExecutionPlanVersions(
  authorizedFetch: AuthorizedFetch,
  executionPlanId: string
): Promise<ExecutionPlanVersion[]> {
  return await json<ExecutionPlanVersion[]>(authorizedFetch,
    `/api/v1/execution-plans/${executionPlanId}/versions`);
}

export async function diffExecutionPlanVersions(
  authorizedFetch: AuthorizedFetch,
  executionPlanId: string,
  fromVersion: number,
  toVersion: number
): Promise<ExecutionPlanDiff> {
  return await json<ExecutionPlanDiff>(authorizedFetch,
    `/api/v1/execution-plans/${executionPlanId}/diff?fromVersion=${fromVersion}&toVersion=${toVersion}`);
}

export async function listExecutionPlanApprovals(
  authorizedFetch: AuthorizedFetch
): Promise<ExecutionPlanApproval[]> {
  return await json<ExecutionPlanApproval[]>(authorizedFetch, "/api/v1/execution-plans/approvals");
}

export async function listExecutionPlanAudit(
  authorizedFetch: AuthorizedFetch,
  executionPlanId: string
): Promise<ExecutionPlanAudit[]> {
  return await json<ExecutionPlanAudit[]>(authorizedFetch,
    `/api/v1/execution-plans/${executionPlanId}/audit`);
}

async function executionPlanCommand<T>(
  authorizedFetch: AuthorizedFetch,
  plan: ExecutionPlan,
  action: string,
  rationale: string
): Promise<T> {
  return await authoring<T>(authorizedFetch, `/api/v1/execution-plans/${plan.id}/${action}`, {
    method: "POST", body: JSON.stringify({expectedVersion: plan.version, rationale})
  });
}

async function command<T>(
  authorizedFetch: AuthorizedFetch,
  workPackage: WorkPackage,
  action: string,
  rationale: string
): Promise<T> {
  return await authoring<T>(authorizedFetch,
    `/api/v1/work-packages/${workPackage.id}/${action}`, {
      method: "POST",
      body: JSON.stringify({
        expectedVersion: workPackage.version,
        rationale
      })
    });
}

async function authoring<T>(
  authorizedFetch: AuthorizedFetch,
  path: string,
  init: RequestInit
): Promise<T> {
  return await json<T>(authorizedFetch, path, {
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Idempotency-Key": crypto.randomUUID(),
      ...Object.fromEntries(new Headers(init.headers).entries())
    }
  });
}

async function json<T>(
  authorizedFetch: AuthorizedFetch,
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<T> {
  const response = await authorizedFetch(input, init);
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as {message?: string} | null;
    throw new Error(problem?.message ?? `Request failed: ${response.status}`);
  }
  return await response.json() as T;
}
